import type { BaseRow } from '@openx/types/tableHelpers';
import { STICKY_POSITION } from '@openx/types/tableHelpers';
import { useTranslation } from 'react-i18next';
import { DEAL_INSIGHTS_COLUMN_MIN_WIDTH } from '../../../../HeatTable/constants';
import { useHeatmapTableColumns } from '../../../../HeatTable/hooks/useHeatmapTableColumns';
import type { ColorConfig, ColumnConfig } from '../../../../HeatTable/types';
import { getAdDurationLabel } from '../utils/adDurationLabels';

export type DealInsightsData = BaseRow & {
  ad_duration: string;
  ctr: string;
  ctr_raw: number | null;
  ecpm: string;
  ecpm_raw: number | null;
  sov: string;
  sov_raw: number | null;
  spend: string;
  spend_raw: number | null;
  viewability: string;
  viewability_raw: number | null;
  impressions: string;
  impressions_raw: number | null;
};

export type MaxValuesConfig = {
  spend: ColorConfig<DealInsightsData>;
  sov: ColorConfig<DealInsightsData>;
  ecpm: ColorConfig<DealInsightsData>;
  ctr: ColorConfig<DealInsightsData>;
  viewability: ColorConfig<DealInsightsData>;
  impressions: ColorConfig<DealInsightsData>;
};

export const useDeliveryByAdDurationTableColumns = (maxValues: MaxValuesConfig) => {
  const { t } = useTranslation();

  const columnsConfig: ColumnConfig<DealInsightsData>[] = [
    {
      key: 'ad_duration',
      title: t('Ad Duration'),
      width: '20%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.AD_DURATION,
      sticky: STICKY_POSITION.LEFT,
      disabled: true,
      freeze: true,
      cellStyle: {
        alignItems: 'flex-start',
        fontWeight: 'fontWeightBold',
        paddingLeft: 0,
        paddingTop: theme => theme.spacing(2),
        paddingBottom: theme => theme.spacing(2),
      },
      headerStyle: {
        paddingLeft: 4,
      },
      render: (row: DealInsightsData) => getAdDurationLabel(row.ad_duration, t),
    },
    {
      key: 'impressions',
      title: t('Impressions'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      colorConfig: {
        ...maxValues.impressions,
        valueKey: 'impressions_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'spend',
      title: t('Spend'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      prefix: '$',
      colorConfig: {
        ...maxValues.spend,
        valueKey: 'spend_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'sov',
      title: t('SOV'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      suffix: '%',
      colorConfig: {
        ...maxValues.sov,
        valueKey: 'sov_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'viewability',
      title: t('Viewability'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      suffix: '%',
      colorConfig: {
        ...maxValues.viewability,
        valueKey: 'viewability_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'ecpm',
      title: t('eCPM'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      prefix: '$',
      colorConfig: {
        ...maxValues.ecpm,
        valueKey: 'ecpm_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'ctr',
      title: t('CTR'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      suffix: '%',
      colorConfig: {
        ...maxValues.ctr,
        valueKey: 'ctr_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
  ];

  return useHeatmapTableColumns(columnsConfig);
};
